import { WageFrequency } from "@api/shared-runtime/enums";
import type { PayWageFrequency } from "@api/shared-runtime/pay-wages";
import { calculateTimeEntryPay } from "@api/shared-runtime/time-entry-pay";
import {
  WAGE_FIELDS,
  type OrganizationWageSetWages,
} from "@/features/organization-wage-sets/utils/wage-fields";
import { getWageFieldKey } from "./ad-hoc-organization-wage-set";
import type { TimesheetEntryBaseWageType } from "./ad-hoc-time-entry-schema";
import { isValidCustomWageAmount } from "./custom-wage";

export const CUSTOM_WAGE_LABEL = "Custom wage";

const MINUTES_PER_HOUR = 60;

export type AdHocPayPreview = {
  // What the wage is, such as "Regular overtime" or "Custom wage".
  wageLabel: string;
  amount: string;
  wageFrequencyId: PayWageFrequency;
  hours: number;
  // Dollars with 2 decimals, exactly as approval will pay it.
  total: string;
};

function selectWage({
  wages,
  wageType,
  isOvertime,
  customWage,
}: {
  wages: OrganizationWageSetWages;
  wageType: TimesheetEntryBaseWageType;
  isOvertime: boolean;
  customWage: { amount: string; wageFrequencyId: PayWageFrequency } | null;
}): Pick<AdHocPayPreview, "wageLabel" | "amount" | "wageFrequencyId"> | null {
  // A custom wage pays the whole shift whatever the wage type, as at approval.
  if (customWage) {
    if (!isValidCustomWageAmount(customWage.amount)) {
      return null;
    }
    return {
      wageLabel: CUSTOM_WAGE_LABEL,
      amount: customWage.amount.trim(),
      wageFrequencyId: customWage.wageFrequencyId,
    };
  }

  const key = getWageFieldKey({ wageType, isOvertime });
  const amount = wages[key];
  const field = WAGE_FIELDS.find((candidate) => candidate.key === key);
  if (amount === null || amount === undefined || !field) {
    return null;
  }
  if (
    wages.wageFrequencyId !== WageFrequency.Hourly &&
    wages.wageFrequencyId !== WageFrequency.OneTime
  ) {
    return null;
  }
  return {
    wageLabel: field.label,
    amount,
    wageFrequencyId: wages.wageFrequencyId,
  };
}

/**
 * What ad-hoc hours will pay once approved: the wage that applies, the hours
 * and the total. The total comes from calculateTimeEntryPay, the same function
 * approval uses, so the preview matches the payout to the cent. Returns null
 * until the form has enough to price the work.
 */
export function getAdHocPayPreview({
  wages,
  wageType,
  isOvertime,
  customWage,
  startsAt,
  endsAt,
}: {
  wages: OrganizationWageSetWages | undefined;
  wageType: TimesheetEntryBaseWageType;
  isOvertime: boolean;
  customWage: { amount: string; wageFrequencyId: PayWageFrequency } | null;
  startsAt: Date | undefined;
  endsAt: Date | undefined;
}): AdHocPayPreview | null {
  if (!wages || !startsAt || !endsAt || endsAt <= startsAt) {
    return null;
  }
  const wage = selectWage({ wages, wageType, isOvertime, customWage });
  if (!wage) {
    return null;
  }

  const pay = calculateTimeEntryPay({
    unitAmount: wage.amount,
    wageFrequencyId: wage.wageFrequencyId,
    startsAt,
    endsAt,
  });
  return {
    ...wage,
    hours: pay.workedMinutes / MINUTES_PER_HOUR,
    total: pay.amount,
  };
}
